/**
 * Keyboard Shortcuts Hook
 * Centralized keyboard shortcut management for the application
 */

import { useEffect, useCallback, useRef } from 'react';

export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean; // Command key on Mac
  action: (event?: KeyboardEvent) => void;
  description: string;
  category: 'navigation' | 'messages' | 'formatting' | 'general';
  enabled?: boolean;
  preventDefault?: boolean;
}

interface UseKeyboardShortcutsOptions {
  enabled?: boolean;
  shortcuts: KeyboardShortcut[];
}

export const useKeyboardShortcuts = ({ enabled = true, shortcuts }: UseKeyboardShortcutsOptions) => {
  const shortcutsRef = useRef(shortcuts);

  // Update shortcuts ref when they change
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Don't trigger shortcuts when typing in input fields (except for special cases)
    const target = event.target as HTMLElement;
    const isInputField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
    const isContentEditable = target.isContentEditable;

    // Find matching shortcut
    const matchedShortcut = shortcutsRef.current.find((shortcut) => {
      if (shortcut.enabled === false) return false;

      const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase();
      const ctrlMatches = shortcut.ctrl ? event.ctrlKey || event.metaKey : !event.ctrlKey && !event.metaKey;
      const shiftMatches = shortcut.shift ? event.shiftKey : !event.shiftKey;
      const altMatches = shortcut.alt ? event.altKey : !event.altKey;

      return keyMatches && ctrlMatches && shiftMatches && altMatches;
    });

    if (matchedShortcut) {
      // For formatting shortcuts, allow them even in input fields
      const isFormattingShortcut = matchedShortcut.category === 'formatting';

      // For navigation shortcuts, don't trigger in input fields
      if (!isFormattingShortcut && (isInputField || isContentEditable)) {
        // Exception: Ctrl+K (quick switcher) should work anywhere
        if (!(matchedShortcut.key === 'k' && (event.ctrlKey || event.metaKey))) {
          return;
        }
      }

      if (matchedShortcut.preventDefault !== false) {
        event.preventDefault();
        event.stopPropagation();
      }

      matchedShortcut.action(event);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);
};

/**
 * Format keyboard shortcut for display
 */
export const formatShortcut = (shortcut: KeyboardShortcut): string => {
  const parts: string[] = [];

  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  if (shortcut.ctrl || shortcut.meta) {
    parts.push(isMac ? '⌘' : 'Ctrl');
  }

  if (shortcut.shift) {
    parts.push(isMac ? '⇧' : 'Shift');
  }

  if (shortcut.alt) {
    parts.push(isMac ? '⌥' : 'Alt');
  }

  // Format special keys
  const keyName = {
    'arrowup': '↑',
    'arrowdown': '↓',
    'arrowleft': '←',
    'arrowright': '→',
    'enter': 'Enter',
    'escape': 'Esc',
    ' ': 'Space',
  }[shortcut.key.toLowerCase()] || shortcut.key.toUpperCase();

  parts.push(keyName);

  return parts.join('+');
};

/**
 * Group shortcuts by category
 */
export const groupShortcutsByCategory = (shortcuts: KeyboardShortcut[]) => {
  const grouped: Record<string, KeyboardShortcut[]> = {
    navigation: [],
    messages: [],
    formatting: [],
    general: [],
  };

  shortcuts.forEach((shortcut) => {
    if (shortcut.enabled !== false) {
      grouped[shortcut.category].push(shortcut);
    }
  });

  return grouped;
};
